/**
 * Instance-level dashboard cells and the expandable component detail.
 *
 * Ported from `componentsCell` / `cvesCell` / `detailTable` of
 * app/web/dashboard.html, with two deliberate changes:
 *
 *   1. CVE ids open their public source; clicks do not toggle the row.
 *   2. The detail table shows KEV/EPSS decision support per CVE, fetched lazily
 *      (see useExploitability) and failing soft to nothing.
 *
 * The group rows, the selection checkboxes and the notify status live in
 * GroupCells.tsx.
 */

import { useLingui as useLinguiContext } from '@lingui/react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { cvssTone } from '../../components/CvssScore';
import { useEffect, type ReactNode } from 'react';

import CveLink from '../../components/CveLink';
import { Chip, ChipList } from '../../components/Chip';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import ManufacturerLogo from '../../components/ManufacturerLogo';
import Spinner from '../../components/Spinner';
import { DASH, fmtDate, fmtDay, fmtScore, fmtVersion } from '../../lib/format';
import type { KomponenteBlock, OverviewRow } from '../../lib/types';
import { fmtPercent, orFallback } from './dashboardModel';
import type { ExploitabilityEntry, ExploitabilityStore } from './useExploitability';

/** A queued mail (DEV-93) is not sent yet. */
const SENT_NOTIFICATION_STATES: ReadonlySet<string> = new Set(['VERSENDET', 'GEMELDET', 'ABGESCHLOSSEN']);

function meldungTone(status: string | null): 'good' | 'warn' {
  return status !== null && SENT_NOTIFICATION_STATES.has(status) ? 'good' : 'warn';
}

/** How many chips a collapsed row shows before it says "+N". */
const MAX_COMPONENT_CHIPS = 3;
const MAX_CVE_CHIPS = 4;

/* ------------------------------------------------------------------------- *
 * Betroffene Komponenten
 * ------------------------------------------------------------------------- */

/**
 * Manufacturer logo, name and a boxed version — the same identity the
 * component catalog shows, so a component looks alike everywhere. Affected
 * versions keep the KEV signal on their box.
 */
function ComponentIdentity({
  komponente,
  compact = false,
}: {
  komponente: KomponenteBlock;
  compact?: boolean;
}): ReactNode {
  const { t } = useLingui();
  const hersteller = komponente.hersteller?.trim() ?? '';
  return (
    <div className={`dash-comp${compact ? ' is-compact' : ''}`}>
      <ManufacturerLogo manufacturer={hersteller} className="dash-comp-logo" />
      <div className="dash-comp-name">
        <strong>{orFallback(komponente.name, t`Unbenannte Komponente`)}</strong>
        <span>{hersteller || t`Hersteller nicht angegeben`}</span>
      </div>
      <span
        className={`dash-comp-version${komponente.kev_betroffen ? ' is-affected' : ''}`}
        title={komponente.kev_betroffen ? t`Betroffene Version` : undefined}
      >
        {fmtVersion(komponente.version)}
      </span>
    </div>
  );
}

export function ComponentsCell({ row }: { row: OverviewRow }): ReactNode {
  useLinguiContext();
  const total = row.komponenten.length;
  if (row.affected_count === 0) {
    return (
      <>
        <Chip tone="good"><Trans>keine KEV-Treffer</Trans></Chip>
        <div className="prod"><Plural value={total} one="# Komponente geprüft" other="# Komponenten geprüft" /></div>
      </>
    );
  }
  const affected = row.komponenten.filter((komponente) => komponente.kev_betroffen);
  const shown = affected.slice(0, MAX_COMPONENT_CHIPS);
  const rest = affected.length - shown.length;
  return (
    <>
      <div>
        <span className="count-pill">{row.affected_count}</span>{' '}
        <span className="muted"><Trans>von {total} betroffen</Trans></span>
      </div>
      <div className="dash-comp-list">
        {shown.map((komponente, index) => (
          <ComponentIdentity key={komponente.id ?? `k-${index}`} komponente={komponente} compact />
        ))}
        {rest > 0 ? <span className="muted"><Trans>+{rest} weitere</Trans></span> : null}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------------- *
 * KEV-CVEs
 * ------------------------------------------------------------------------- */

export function CvesCell({
  cves,
  max = MAX_CVE_CHIPS,
}: {
  cves: readonly string[];
  max?: number;
}): ReactNode {
  if (cves.length === 0) return <span className="muted">{DASH}</span>;
  const shown = cves.slice(0, max);
  const rest = cves.length - shown.length;
  return (
    <ChipList>
      {shown.map((cve) => (
        <CveLink key={cve} cve={cve} />
      ))}
      {rest > 0 ? <span className="muted">+{rest}</span> : null}
    </ChipList>
  );
}

/* ------------------------------------------------------------------------- *
 * Detail area — component sub-table with KEV/EPSS chips and CVE links
 * ------------------------------------------------------------------------- */


/** KEV/EPSS chips for one CVE. Renders nothing while nothing is known yet. */
export function ExploitabilityChips({ entry }: { entry: ExploitabilityEntry | undefined }): ReactNode {
  const { t } = useLingui();
  if (entry === undefined) return null;
  if (entry.state === 'loading') {
    return (
      <span className="chip" title={t`Exploitability wird geladen`}>
        <Spinner label={t`Exploitability wird geladen`} /> EPSS
      </span>
    );
  }
  if (entry.state === 'unavailable') {
    return (
      <span className="chip muted" title={entry.message}>
        <Trans>EPSS n. v.</Trans>
      </span>
    );
  }

  const { facts } = entry;
  const chips: ReactNode[] = [];
  if (facts.known_exploited) {
    const added = facts.kev_date_added ? fmtDay(facts.kev_date_added) : null;
    const due = facts.kev_due_date ? fmtDay(facts.kev_due_date) : null;
    chips.push(
      <Chip
        key="kev"
        tone="alert"
        title={[
          facts.kev_sources.filter(Boolean).join(', ') || 'KEV',
          added ? t`veröffentlicht am ${added}` : null,
          due ? t`Frist ${due}` : null,
        ].filter(Boolean).join(', ')}
      >
        KEV
      </Chip>,
    );
  }
  if (facts.kev_known_ransomware === 'Known') {
    chips.push(
      <Chip key="ransom" tone="alert" title={t`Bekannter Einsatz in Ransomware-Kampagnen`}>
        Ransomware
      </Chip>,
    );
  }
  if (facts.epss_score !== null) {
    const hot = facts.epss_score >= facts.epss_threshold;
    const threshold = fmtPercent(facts.epss_threshold);
    const percentile = facts.epss_percentile !== null ? fmtPercent(facts.epss_percentile) : null;
    chips.push(
      <Chip
        key="epss"
        tone={hot ? 'warn' : 'neutral'}
        title={[
          t`EPSS-Wahrscheinlichkeit (FIRST.org), Schwelle ${threshold}`,
          percentile ? t`Perzentil ${percentile}` : null,
        ].filter(Boolean).join(' · ')}
      >
        EPSS {fmtPercent(facts.epss_score)}
      </Chip>,
    );
  }
  if (facts.cvss_base_score !== null) {
    chips.push(
      <Chip key="cvss" tone={cvssTone(facts.cvss_base_score)} title={facts.severity ?? 'CVSS Base Score'}>
        CVSS {fmtScore(facts.cvss_base_score)}
      </Chip>,
    );
  }
  // ENISA EUVD: shown when it adds something DT does not have — a CVSS 4.0
  // score (NVD is still on 3.1 for most OT CVEs) or the EU id for CRA
  // reporting. Clickable, because the next question is always "what does the EU
  // record say".
  if (entry.euvd !== null) {
    const euvd = entry.euvd;
    const score = euvd.cvss_base_score;
    const label = score !== null ? `EUVD ${fmtScore(score)}` : 'EUVD';
    const aliases = euvd.aliases.join(', ');
    chips.push(
      <a
        key="euvd"
        className="chip eu"
        href={euvd.url ?? undefined}
        target="_blank"
        rel="noreferrer"
        title={[
          euvd.id,
          euvd.cvss_version ? `CVSS ${euvd.cvss_version}` : null,
          euvd.cvss_vector,
          euvd.assigner ? `CNA ${euvd.assigner}` : null,
          aliases !== '' ? t`auch ${aliases}` : null,
          t`ENISA EU Vulnerability Database (öffnet in neuem Tab)`,
        ].filter(Boolean).join(' · ')}
      >
        {label}
      </a>,
    );
    if (euvd.known_exploited_eu) {
      const euvdId = euvd.id;
      chips.push(
        <Chip key="euvd-exploited" tone="alert" title={t`${euvdId}: von der EU als ausgenutzt geführt`}>
          EU-exploited
        </Chip>,
      );
    }
  }
  if (!entry.inDependencyTrack) {
    chips.push(
      <Chip key="dt" tone="neutral" title={t`Dependency-Track kennt diese Schwachstelle nicht`}>
        <Trans>nicht in DT</Trans>
      </Chip>,
    );
  }
  return <>{chips}</>;
}

function KevStatusCell({
  komponente,
  store,
}: {
  komponente: KomponenteBlock;
  store: ExploitabilityStore;
}): ReactNode {
  const { t } = useLingui();
  if (!komponente.kev_betroffen) return <Chip tone="good"><Trans>sauber</Trans></Chip>;
  if (komponente.kev_cves.length === 0) {
    return <Chip tone="alert" title={t`Als betroffen markiert, aber ohne CVE-Liste`}><Trans>betroffen</Trans></Chip>;
  }
  return (
    <div className="dash-cve-facts">
      {komponente.kev_cves.map((cve) => (
        <div className="dash-cve-fact" key={cve}>
          <CveLink cve={cve} />
          {store.enabled ? <ExploitabilityChips entry={store.entries.get(cve.toUpperCase())} /> : null}
        </div>
      ))}
    </div>
  );
}

export interface KomponentenDetailProps {
  row: OverviewRow;
  store: ExploitabilityStore;
}

/** The expanded area of a product row: its resolved configuration. */
export function KomponentenDetail({ row, store }: KomponentenDetailProps): ReactNode {
  const { t } = useLingui();
  const { request } = store;
  // The joined ids — not the array identity — are the real dependency: every
  // refresh hands us a new array holding the same CVEs and must not refetch.
  const cveKey = row.kev_cves.join(' ');
  useEffect(() => {
    request(cveKey === '' ? [] : cveKey.split(' '));
  }, [request, cveKey]);

  const columns: DataTableColumn<KomponenteBlock>[] = [
    {
      key: 'komponente',
      header: t`Komponente / Version`,
      width: '34%',
      render: (komponente) => <ComponentIdentity komponente={komponente} />,
    },
    {
      key: 'kev',
      header: t`KEV-Status`,
      width: '30%',
      render: (komponente) => <KevStatusCell komponente={komponente} store={store} />,
    },
    {
      key: 'benachrichtigung',
      header: t`Benachrichtigung`,
      width: '18%',
      render: (komponente) => komponente.meldungen.length > 0 ? (
        <ChipList>
          {komponente.meldungen.map((meldung, index) => {
            const titel = orFallback(meldung.titel, t`Benachrichtigung`);
            const created = fmtDate(meldung.erstellt_am);
            return (
              <Chip
                key={meldung.id ?? `meldung-${index}`}
                tone={meldungTone(meldung.status)}
                title={t`${titel} · erstellt ${created}`}
              >
                {meldung.status ?? 'OFFEN'}
              </Chip>
            );
          })}
        </ChipList>
      ) : (
        <span className="muted">{komponente.kev_betroffen ? t`keine` : DASH}</span>
      ),
    },
    {
      key: 'scan',
      header: t`Letzter Scan`,
      width: '18%',
      cellClassName: 'muted',
      render: (komponente) => fmtDate(komponente.letzter_kev_scan),
    },
  ];

  if (row.komponenten.length === 0) {
    return (
      <div className="dash-detail-note">
        <Trans>
          Keine Komponenten: Für diese Produktinstanz ist keine Produktversion mit gültiger Stückliste
          hinterlegt.
        </Trans>
      </div>
    );
  }

  return (
    <>
      <div className="dash-subwrap">
        <DataTable
          rows={row.komponenten}
          columns={columns}
          rowKey={(komponente) => komponente.id ?? `komponente-${row.komponenten.indexOf(komponente)}`}
          mobileBreakpoint={720}
          pageSize={10}
          paginationResetKey={String(row.produkt.id)}
          resetPageOnRowsChange={false}
          caption={t`Komponenten der Produktkonfiguration`}
        />
      </div>

      {row.benachrichtigungen.length > 0 ? (
        <div className="dash-detail-note">
          <b><Trans>Benachrichtigungen dieser Instanz:</Trans></b>{' '}
          {row.benachrichtigungen.map((block, index) => {
            const sentAt = block.versendet_am ? fmtDate(block.versendet_am) : null;
            const sentLabel = sentAt ? t`versendet ${sentAt}` : null;
            return (
              <span key={block.id ?? `ben-${index}`} className="dash-ben">
                #{block.id ?? '?'} {block.status}
                {block.empfaenger ? ` · ${block.empfaenger}` : ''}
                {sentLabel ? ` · ${sentLabel}` : ''}
              </span>
            );
          })}
        </div>
      ) : null}
    </>
  );
}
